// Boomin' — My Coupons / Wallet screen (/wallet)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  BRAND,
  BRAND_SOFT,
  PictX,
  BottomNavX,
  AppHeader,
  AppFooter,
  shade,
  useBoominFx,
} from "./shared";
import {
  FavoriteItem,
  UsedItem,
  categoryLabel,
  fetchFavorites,
  fetchUsedList,
  inferCategory,
  isUserLoggedIn,
} from "./api";

const brand = BRAND;
const brandSoft = BRAND_SOFT;

type Tab = "active" | "used";

export const WalletScreen: React.FC = () => {
  useBoominFx();
  const [tab, setTab] = useState<Tab>("active");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [used, setUsed] = useState<UsedItem[]>([]);

  const loggedIn = isUserLoggedIn();

  const load = useCallback(async () => {
    if (!loggedIn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [favs, usedList] = await Promise.all([fetchFavorites(), fetchUsedList()]);
      setFavorites(favs);
      setUsed(usedList);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [loggedIn]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Helmet>
        <title>マイクーポン | Boomin'</title>
      </Helmet>
      <AppHeader />
      <div
        style={{
          background: "#FFFBF7",
          minHeight: "100vh",
          paddingBottom: 0,
          position: "relative",
        }}
      >
        <WalletHeader activeCount={favorites.length} usedCount={used.length} />

        {!loggedIn ? (
          <SignInPrompt />
        ) : (
          <>
            <TabBar
              tab={tab}
              onChange={setTab}
              activeCount={favorites.length}
              usedCount={used.length}
            />

            {error ? (
              <ErrorState message={error} onRetry={load} />
            ) : loading ? (
              <ListSkeleton />
            ) : tab === "active" ? (
              <ActiveList items={favorites} />
            ) : (
              <UsedList items={used} />
            )}
          </>
        )}

        <AppFooter withBottomNav />
      </div>

      <BottomNavX active="tickets" brand={brand} />
    </>
  );
};

const WalletHeader: React.FC<{ activeCount: number; usedCount: number }> = ({
  activeCount,
}) => (
  <div
    style={{
      padding: "calc(env(safe-area-inset-top, 0px) + 28px) 20px 0",
      position: "relative",
    }}
  >
    <div
      style={{
        fontSize: 11,
        color: "#888",
        fontWeight: 700,
        letterSpacing: "0.12em",
        marginBottom: 4,
      }}
    >
      YOUR TREASURES
    </div>
    <h1
      style={{
        fontSize: 30,
        fontWeight: 800,
        margin: 0,
        letterSpacing: "0.01em",
        lineHeight: 1.15,
      }}
    >
      マイクーポン<span style={{ color: brand }}>。</span>
    </h1>
    <div
      style={{
        fontSize: 13,
        color: "#666",
        marginTop: 8,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>
        <b style={{ color: brand, fontSize: 16 }}>{activeCount}</b> 件 保存中
      </span>
    </div>

    <div
      style={{
        position: "absolute",
        right: 16,
        top: "calc(env(safe-area-inset-top, 0px) + 24px)",
        opacity: 0.5,
      }}
    >
      <PictX name="sparkle" size={28} stroke={brand} strokeWidth={1.6} />
    </div>
  </div>
);

const TabBar: React.FC<{
  tab: Tab;
  onChange: (t: Tab) => void;
  activeCount: number;
  usedCount: number;
}> = ({ tab, onChange, activeCount, usedCount }) => (
  <div style={{ padding: "20px 20px 4px", display: "flex", gap: 8 }}>
    {(
      [
        { id: "active" as const, l: "お気に入り", count: activeCount },
        { id: "used" as const, l: "使用済み", count: usedCount },
      ]
    ).map((t) => {
      const on = tab === t.id;
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            background: on ? brand : "#fff",
            color: on ? "#fff" : "#666",
            border: on ? 0 : "1px solid #F0E6DC",
            padding: "9px 14px",
            borderRadius: 999,
            fontWeight: 700,
            fontSize: 12,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t.l}
          <span
            style={{
              background: on ? "rgba(255,255,255,0.25)" : brandSoft,
              color: on ? "#fff" : brand,
              padding: "2px 7px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
            }}
          >
            {t.count}
          </span>
        </button>
      );
    })}
  </div>
);

const ActiveList: React.FC<{ items: FavoriteItem[] }> = ({ items }) => {
  if (items.length === 0) return <EmptyState kind="active" />;
  return (
    <div
      style={{
        padding: "14px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {items.map((t, i) => (
        <WalletTicket key={t.id} t={t} idx={i} />
      ))}
    </div>
  );
};

const UsedList: React.FC<{ items: UsedItem[] }> = ({ items }) => {
  // Sort by most-recently-used for a cleaner history view.
  const rows = useMemo(() => {
    return [...items].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );
  }, [items]);
  if (items.length === 0) return <EmptyState kind="used" />;
  return (
    <div
      style={{
        padding: "14px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {rows.map((u, i) => (
        <UsedRow key={`${u.title}-${u.created}-${i}`} u={u} />
      ))}
    </div>
  );
};

// Pretty-print the favorite as a wallet ticket. Band color is derived from
// the inferred category so each ticket feels distinct.
const CAT_COLORS: Record<string, string> = {
  cafe: BRAND,
  meal: "#0FAC6D",
  sweet: "#3DBED9",
  bar: "#7A4DFF",
  shop: "#FF7A52",
  salon: "#E54E8A",
  activity: "#F2A93B",
};

const WalletTicket: React.FC<{ t: FavoriteItem; idx: number }> = ({ t, idx }) => {
  const rotate = idx % 2 === 0 ? -0.5 : 0.5;
  const NOTCH = 12;
  const BAND_W = 64;
  // Reuse the catalog inferrer; we only have title + description + hub_name here.
  const fakeCoupon = {
    title: t.title,
    description: t.description,
    hub_name: t.hub_name,
  } as any;
  const cat = inferCategory(fakeCoupon, null);
  const band = CAT_COLORS[cat] || brand;

  return (
    <a
      href={`/c/${t.id}`}
      style={{
        position: "relative",
        cursor: "pointer",
        transform: `rotate(${rotate}deg)`,
        transition: "transform 200ms",
        textDecoration: "none",
        color: "inherit",
        display: "block",
      }}
    >
      <div
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
          overflow: "hidden",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <div
          style={{
            width: BAND_W,
            flexShrink: 0,
            background: `linear-gradient(160deg, ${band} 0%, ${shade(band, 18)} 100%)`,
            color: "#fff",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 60 120"
            preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, opacity: 0.25 }}
          >
            <circle cx="14" cy="20" r="9" fill="#fff" />
            <circle cx="46" cy="80" r="14" fill="#fff" />
            <circle cx="22" cy="100" r="6" fill="#fff" />
          </svg>
          <div
            style={{
              writingMode: "vertical-rl",
              textOrientation: "mixed",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.18em",
              position: "relative",
            }}
          >
            {categoryLabel(cat)}
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            left: BAND_W,
            top: 0,
            bottom: 0,
            width: 0,
            borderLeft: "1.5px dashed #E5DBD0",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            left: BAND_W,
            top: -NOTCH / 2,
            width: NOTCH,
            height: NOTCH,
            marginLeft: -NOTCH / 2,
            background: "#FFFBF7",
            borderRadius: "50%",
            zIndex: 2,
          }}
        />
        <div
          style={{
            position: "absolute",
            left: BAND_W,
            bottom: -NOTCH / 2,
            width: NOTCH,
            height: NOTCH,
            marginLeft: -NOTCH / 2,
            background: "#FFFBF7",
            borderRadius: "50%",
            zIndex: 2,
          }}
        />

        <div
          style={{
            flex: 1,
            padding: "16px 16px 16px 22px",
            minWidth: 0,
            position: "relative",
          }}
        >
          <div style={{ fontSize: 11, color: "#888", fontWeight: 700, marginBottom: 3 }}>
            {t.hub_name}
          </div>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              lineHeight: 1.3,
              color: "#1A1A1A",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {t.title}
          </div>
        </div>

        <div style={{ alignSelf: "center", paddingRight: 14, color: brand }}>
          <PictX name="chevron" size={18} stroke={brand} strokeWidth={2.2} />
        </div>
      </div>
    </a>
  );
};

const UsedRow: React.FC<{ u: UsedItem }> = ({ u }) => {
  const d = new Date(u.created);
  const label = Number.isNaN(d.getTime())
    ? ""
    : `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        background: "#fff",
        borderRadius: 14,
        padding: 12,
        border: "1px solid #F0E6DC",
        opacity: 0.85,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 10,
          overflow: "hidden",
          flexShrink: 0,
          background: brandSoft,
        }}
      >
        {u.image ? (
          <img
            src={u.image}
            alt=""
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PictX name="ticket" size={24} stroke={brand} strokeWidth={1.4} />
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.3,
            marginBottom: 4,
            color: "#1A1A1A",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {u.title}
        </div>
        <div style={{ fontSize: 11, color: "#888" }}>
          {label} ・ {u.from_qr_code ? "QR利用" : "店頭利用"}
        </div>
      </div>
    </div>
  );
};

const SignInPrompt: React.FC = () => (
  <div
    style={{
      margin: "32px 20px 0",
      padding: "28px 22px",
      background: "#fff",
      border: "1px solid #F0E6DC",
      borderRadius: 22,
      textAlign: "center",
      lineHeight: 1.7,
    }}
  >
    <div style={{ fontSize: 32, marginBottom: 8 }}>🎟️</div>
    <div style={{ fontSize: 16, fontWeight: 800, color: "#1A1A1A", marginBottom: 6 }}>
      クーポンを保存しよう
    </div>
    <div style={{ fontSize: 12, color: "#666", marginBottom: 18 }}>
      ログインすると、お気に入りのクーポンを集めて
      <br />
      いつでも見返せます。
    </div>
    <a
      href="/public/oauth/instabase"
      style={{
        display: "inline-block",
        background: brand,
        color: "#fff",
        padding: "12px 28px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 800,
        textDecoration: "none",
        boxShadow: `0 6px 18px ${brand}55`,
      }}
    >
      ログイン / 新規登録
    </a>
  </div>
);

const EmptyState: React.FC<{ kind: "active" | "used" }> = ({ kind }) => (
  <div
    style={{
      margin: "20px 20px 0",
      padding: "32px 20px",
      background: "#fff",
      border: "1px dashed #F0E6DC",
      borderRadius: 18,
      textAlign: "center",
      color: "#777",
      fontSize: 13,
      lineHeight: 1.7,
    }}
  >
    {kind === "active"
      ? "まだ保存したクーポンがありません。"
      : "まだ使ったクーポンがありません。"}
    <br />
    <a href="/discover" style={{ color: brand, fontWeight: 800, textDecoration: "none" }}>
      クーポンを探す →
    </a>
  </div>
);

const ListSkeleton: React.FC = () => (
  <div
    style={{
      padding: "14px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 14,
    }}
  >
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        style={{
          height: 92,
          borderRadius: 18,
          background: "#fff",
          border: "1px solid #F0E6DC",
        }}
      />
    ))}
  </div>
);

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div
    style={{
      margin: "20px 20px 0",
      padding: "20px",
      background: "#fff",
      border: "1px solid #FFDAD0",
      borderRadius: 18,
      color: "#A6321E",
      fontSize: 13,
      lineHeight: 1.6,
    }}
  >
    <div style={{ marginBottom: 10 }}>{message}</div>
    <button
      onClick={onRetry}
      style={{
        background: brand,
        color: "#fff",
        border: 0,
        borderRadius: 999,
        padding: "8px 16px",
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      もう一度
    </button>
  </div>
);

export default WalletScreen;
